import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@platform/ui';
import { useKeyboard } from '@platform/hooks/useKeyboard';
import { routes } from '@platform/core/lib/routes';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { AiButton } from './shell/AiButton';
import { GroupNav } from './shell/GroupNav';
import { HelpButton } from './shell/HelpButton';
import type { MenuKey } from './shell/MenuKey';
import { ModeToggleButton } from './shell/ModeToggleButton';
import { recordTypeTotal } from './shell/recordTypeTotal';
import { useWorkspaceCatalog } from './shell/useWorkspaceCatalog';
import { NavModeMenu } from './shell/NavModeMenu';
import { toggleNavigator } from './shell/navigator/navigatorStore';
import { NotificationsMenu } from './shell/NotificationsMenu';
import { PinnedMenu } from './shell/PinnedMenu';
import { ProfileMenu } from './shell/ProfileMenu';
import { TopSearch } from './shell/TopSearch';
import { websiteUrl } from './shell/websiteUrl';
import { WorkspaceSwitcher } from './shell/WorkspaceSwitcher';
import type { ITopBarProps } from './ITopBarProps';
import './shell/topbar.css';

/**
 * Верхня панель v4 (UIUX layout/top-bar, прототип Groundwork.dc.html): 56px бренд-синя. Ліворуч — перемикач робочих просторів, смуга
 * груп модулів режиму навігації («Головна» — будинок, активна група — біла пігулка «група · модуль»), закріплені модулі (зірка), усі модулі,
 * «Режим навігації». Праворуч — пошук, Адміністрування, Налаштування рішення, вебсайт, ШІ, Довідка, Сповіщення, аватар.
 * Одночасно відкрите лише одне меню; клік поза ним чи Esc закриває. Ctrl/⌘ K відкриває й закриває навігатор модулів (patterns/keyboard).
 */
export function TopBar({ ws }: ITopBarProps) {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const [open, setOpen] = useState<MenuKey>(null);
  const toggle = useCallback((key: MenuKey) => setOpen((cur) => (cur === key ? null : key)), []);
  const close = useCallback(() => setOpen(null), []);
  const onNavigatorKey = useCallback(() => { setOpen(null); toggleNavigator(); }, []);
  useKeyboard('k', onNavigatorKey);
  const { catalog } = useWorkspaceCatalog(ws);
  const total = recordTypeTotal(catalog) ?? 0;
  const allModules = t('shell.allModules', { count: total });
  const menu = (key: Exclude<MenuKey, null>) => ({ open: open === key, onToggle: () => toggle(key), onClose: close });
  return (
    <header className="po-topbar tb">
      <WorkspaceSwitcher ws={ws} {...menu('ws')} />
      <GroupNav ws={ws} open={open} onToggle={toggle} onClose={close} />
      <PinnedMenu ws={ws} {...menu('pinned')} />
      <Link className="po-topbar__icon tb-icon" to={routes.modules()} title={allModules} aria-label={allModules}><Icon name="layout-grid" size={16} /></Link>
      <NavModeMenu ws={ws} {...menu('mode')} />
      <div className="tb-right">
        <TopSearch />
        <ModeToggleButton ws={ws} item="administration" mode="admin" />
        <ModeToggleButton ws={ws} item="customization" mode="cust" />
        <a className="po-topbar__icon tb-icon tb-wide" href={websiteUrl(lang)} target="_blank" rel="noopener noreferrer" title={t('shell.websiteTitle')} aria-label={t('shell.website')}>
          <Icon name="globe" size={16} />
        </a>
        <AiButton />
        <HelpButton ws={ws} />
        <NotificationsMenu ws={ws} {...menu('notifications')} />
        <ProfileMenu {...menu('profile')} />
      </div>
    </header>
  );
}
